import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SetupPage from './pages/SetupPage';
import Dashboard from './pages/Dashboard';
import WorkspaceDashboard from './pages/WorkspaceDashboard';
import BoardEditor from './pages/BoardEditor';
import ClientView from './pages/ClientView';
import ApprovalView from './pages/ApprovalView';
import MaestraGuide from './pages/MaestraGuide';
import ImageLibrary from './pages/ImageLibrary';
import AdminPanel from './pages/AdminPanel';
import InviteAccept from './pages/InviteAccept';
import RequestAccess from './pages/RequestAccess';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ExtendAccess from './pages/ExtendAccess';
import TemplateEditor from './pages/TemplateEditor';
import BrandingSettings from './pages/BrandingSettings';
import BoardSettings from './pages/BoardSettings';
import CreatorSpaceModule from './pages/Creator';
import UserSettings from './pages/UserSettings';
import LegalPage from './pages/LegalPage';
import AdminInternalDocsDownload from './pages/AdminInternalDocsDownload';
import AdminBlueprint from './pages/AdminBlueprint';
import ClientsList from './pages/ClientsList';
import ClientSpace from './pages/ClientSpace';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = Cookies.get('kahla_token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const token = Cookies.get('kahla_token');
  const role = Cookies.get('kahla_role');
  if (!token) return <Navigate to="/login" replace />;
  if (role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <div className="app-container">
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/setup" element={<SetupPage />} />
          <Route path="/invite/:token" element={<InviteAccept />} />
          <Route path="/request-access" element={<RequestAccess />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Legal Pages (public, admin-editable) */}
          <Route path="/impressum" element={<LegalPage slug="impressum" />} />
          <Route path="/privacy" element={<LegalPage slug="privacy" />} />
          <Route path="/terms" element={<LegalPage slug="terms" />} />
          
          {/* Client Route (public, accessed via share link) */}
          <Route path="/board/:shareToken" element={<ClientView />} />

          {/* Approval Route (private, identity-linked secure token sent via email) */}
          <Route path="/approval/:approvalToken" element={<ApprovalView />} />

          {/* Maestra Guide — hidden route, accessible from Settings only */}
          <Route path="/guide" element={<ProtectedRoute><MaestraGuide /></ProtectedRoute>} />
          
          {/* Protected Owner Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <WorkspaceDashboard />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/boards"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/boards/settings"
            element={
              <ProtectedRoute>
                <BoardSettings />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/editor/:boardId" 
            element={
              <ProtectedRoute>
                <BoardEditor />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/library" 
            element={
              <ProtectedRoute>
                <ImageLibrary />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/creator"
            element={
              <ProtectedRoute>
                <CreatorSpaceModule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/decisions"
            element={
              <ProtectedRoute>
                <CreatorSpaceModule view="decisions" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/projects"
            element={
              <ProtectedRoute>
                <CreatorSpaceModule view="projects" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/todo"
            element={
              <ProtectedRoute>
                <CreatorSpaceModule view="todo" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/ideas"
            element={
              <ProtectedRoute>
                <CreatorSpaceModule view="ideas" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/timeline"
            element={
              <ProtectedRoute>
                <CreatorSpaceModule view="timeline" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/area/:areaKey"
            element={
              <ProtectedRoute>
                <CreatorSpaceModule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/item/:itemId"
            element={
              <ProtectedRoute>
                <CreatorSpaceModule />
              </ProtectedRoute>
            }
          />

          <Route
            path="/clients"
            element={
              <ProtectedRoute>
                <ClientsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/:clientId"
            element={
              <ProtectedRoute>
                <ClientSpace />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/branding" 
            element={
              <ProtectedRoute>
                <BrandingSettings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <UserSettings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/extend-access" 
            element={
              <ProtectedRoute>
                <ExtendAccess />
              </ProtectedRoute>
            } 
          />
          
          {/* Admin Routes */}
          <Route
            path="/admin/blueprint"
            element={
              <AdminRoute>
                <AdminBlueprint />
              </AdminRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminPanel />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/templates/:templateId"
            element={
              <AdminRoute>
                <TemplateEditor />
              </AdminRoute>
            }
          />
          {/* Founder-only single-shot download trigger (no UI listing). */}
          <Route
            path="/admin/internal-docs/:slug"
            element={
              <AdminRoute>
                <AdminInternalDocsDownload />
              </AdminRoute>
            }
          />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
